import type { MapDocument, MapGeometry } from "../shared/types";
import { mapPreviewOpacity } from "../shared/mapPresentation";
import type { VenueMapLogos } from "./useVenueMapLogos";
import type { VenueMapRouteData } from "./VenueMapRoute";
import { mapStandHeight, type VenueMapCamera } from "./venueMapProjection";
import { roundedMapRouteSegments } from "./venueMapRouting";
import {
  buildVenueMapWebGLMeshes,
  updateVenueMapWebGLStandVertices,
  venueMapWebGLStandDrawRanges,
  buildVenueMapWebGLSelection,
  venueMapWebGLBufferSize,
  venueMapWebGLProjection,
  venueMapWebGLViewPosition,
  type VenueMapWebGLViewport,
} from "./venueMapWebGLGeometry";
import {
  buildVenueMapWebGLLabelAtlas,
  updateVenueMapWebGLLabelVertices,
} from "./venueMapWebGLLabels";
import { mapAppearanceSourceDocument } from "./venueMapAppearance";
import { buildVenueMapWebGLEntranceAtlas } from "./venueMapWebGLEntrances";
import {
  venueMapLighting,
  VENUE_MAP_WALL_AMBIENT,
  VENUE_MAP_WALL_DIFFUSE,
} from "./venueMapLighting";

const solidVertex = `
attribute vec3 a_position;
attribute vec3 a_normal;
attribute vec4 a_color;
uniform mat4 u_projection;
uniform vec3 u_light;
uniform vec4 u_eye;
varying mediump vec4 v_color;
varying mediump float v_facing;
void main() {
  float light = a_normal.z > 0.5 ? 1.0 : ${VENUE_MAP_WALL_AMBIENT.toFixed(2)} + max(0.0, dot(a_normal, u_light)) * ${VENUE_MAP_WALL_DIFFUSE.toFixed(2)};
  v_color = vec4(a_color.rgb * light, a_color.a);
  v_facing = dot(a_normal, normalize(u_eye.xyz - a_position * u_eye.w));
  gl_Position = u_projection * vec4(a_position, 1.0);
}`;

const solidFragment = `
precision mediump float;
uniform float u_opacity;
varying mediump vec4 v_color;
varying mediump float v_facing;
void main() {
  // Ghosts reveal the scene behind them, not their own back walls. Normals
  // remain reliable even when imported polygons use inconsistent winding.
  if (v_color.a < 1.0 && v_facing <= 0.0) discard;
  gl_FragColor = vec4(v_color.rgb, v_color.a * u_opacity);
}
`;

const labelVertex = `
attribute vec3 a_position;
attribute vec2 a_uv;
attribute vec3 a_ink;
attribute float a_opacity;
uniform mat4 u_projection;
varying UV_PRECISION vec2 v_uv;
varying mediump vec3 v_ink;
varying mediump float v_opacity;
void main() {
  v_opacity = a_opacity;
  v_uv = a_uv;
  v_ink = a_ink;
  gl_Position = u_projection * vec4(a_position, 1.0);
}`;

const labelFragment = `
precision mediump float;
uniform sampler2D u_atlas;
varying UV_PRECISION vec2 v_uv;
varying mediump vec3 v_ink;
varying mediump float v_opacity;
void main() {
  vec4 texel = texture2D(u_atlas, v_uv);
  if (texel.a < 0.005) discard;
  vec3 color = texel.rgb;
  if (v_ink.r < 0.0) {
    float saturation = clamp(-v_ink.r - 1.0, 0.0, 1.0);
    float grey = dot(color, vec3(0.2126, 0.7152, 0.0722));
    color = mix(vec3(grey), color, saturation);
  } else {
    color *= v_ink;
  }
  gl_FragColor = vec4(color, texel.a) * v_opacity;
}`;

// Entrances stay upright at their projected floor anchors. Draw them in the
// same framebuffer as the map: a moving SVG overlay can leave stale tiles in
// Safari's compositor during scrolling, even when its DOM is up to date.
const entranceVertex = `
attribute vec3 a_position;
attribute vec2 a_offset;
attribute vec2 a_uv;
attribute float a_opacity;
uniform mat4 u_projection;
uniform vec2 u_viewport;
uniform float u_scale;
varying UV_PRECISION vec2 v_uv;
varying mediump float v_opacity;
void main() {
  v_opacity = a_opacity;
  vec4 clip = u_projection * vec4(a_position, 1.0);
  clip.xy += a_offset * u_scale / u_viewport * vec2(2.0, -2.0) * clip.w;
  gl_Position = clip;
  v_uv = a_uv;
}`;

const entranceFragment = `
precision mediump float;
uniform sampler2D u_atlas;
varying UV_PRECISION vec2 v_uv;
varying mediump float v_opacity;
void main() {
  vec4 color = texture2D(u_atlas, v_uv);
  if (color.a < 0.005) discard;
  gl_FragColor = color * v_opacity;
}`;

const routeVertex = `
attribute vec3 a_position;
attribute vec3 a_previous;
attribute vec3 a_next;
attribute vec2 a_offset;
uniform mat4 u_projection;
uniform vec2 u_viewport;
uniform mediump float u_width;
uniform float u_surfaceDepth;
varying mediump vec2 v_offset;
vec2 screenPoint(vec4 clip) { return clip.xy / clip.w * u_viewport * vec2(0.5, -0.5); }
void main() {
  vec4 clip = u_projection * vec4(a_position, 1.0);
  vec2 point = screenPoint(clip);
  vec2 before = point - screenPoint(u_projection * vec4(a_previous, 1.0));
  vec2 after = screenPoint(u_projection * vec4(a_next, 1.0)) - point;
  if (length(before) < 0.0001) before = after;
  if (length(after) < 0.0001) after = before;
  vec2 incoming = before / max(0.0001, length(before));
  vec2 outgoing = after / max(0.0001, length(after));
  vec2 tangent = incoming + outgoing;
  if (length(tangent) < 0.0001) tangent = outgoing;
  tangent = tangent / max(0.0001, length(tangent));
  vec2 normal = vec2(-tangent.y, tangent.x);
  float miter = max(0.5, abs(dot(normal, vec2(-outgoing.y, outgoing.x))));
  vec2 offset = (tangent * a_offset.x + normal * a_offset.y / miter) * u_width;
  vec2 delta = offset / u_viewport * vec2(2.0, -2.0);
  if (u_surfaceDepth > 0.5) {
    // Expanding a roof outline in screen space must also follow its depth
    // plane. Otherwise the roof and front wall clip the expanded stroke.
    // These projective derivatives describe the constant-height roof in NDC.
    vec3 roofX = u_projection[0].xyz * clip.w - clip.xyz * u_projection[0].w;
    vec3 roofY = u_projection[1].xyz * clip.w - clip.xyz * u_projection[1].w;
    vec3 plane = cross(roofX, roofY);
    if (abs(plane.z) > length(plane) * 0.00001) {
      clip.z -= dot(plane.xy, delta) / plane.z * clip.w;
    }
  }
  clip.xy += delta * clip.w;
  gl_Position = clip;
  v_offset = a_offset;
}`;

const routeFragment = `
precision mediump float;
uniform vec4 u_color;
uniform mediump float u_width;
uniform float u_arrow;
varying mediump vec2 v_offset;
void main() {
  float edge = 1.0 - smoothstep(max(0.0, 1.0 - 0.65 / max(0.65, u_width)), 1.0, length(v_offset));
  gl_FragColor = vec4(u_color.rgb, u_color.a * (u_arrow > 0.5 ? 1.0 : edge));
}`;

type RoutePoint = { x: number; y: number };

/** Static screen-width ribbons; the vertex shader follows camera changes. */
export function buildVenueMapWebGLRoute(
  route: VenueMapRouteData,
  geometry: MapGeometry,
) {
  const segments =
    route.segments ??
    roundedMapRouteSegments(route.points, route.geometry || geometry);
  const points: RoutePoint[] = [];
  const source = (point: RoutePoint) => ({
    x: point.x * geometry.width,
    y: point.y * geometry.height,
  });
  if (segments.length) points.push(source(segments[0].start));
  for (const segment of segments) {
    if (segment.control) {
      for (let step = 1; step <= 12; step++) {
        const t = step / 12;
        points.push(
          source({
            x:
              (1 - t) ** 2 * segment.start.x +
              2 * (1 - t) * t * segment.control.x +
              t ** 2 * segment.end.x,
            y:
              (1 - t) ** 2 * segment.start.y +
              2 * (1 - t) * t * segment.control.y +
              t ** 2 * segment.end.y,
          }),
        );
      }
    } else points.push(source(segment.end));
  }
  const line: number[] = [];
  const arrow: number[] = [];
  const z = mapStandHeight("exhibitor") / 2;
  const vertex = (
    target: number[],
    point: RoutePoint,
    previous: RoutePoint,
    next: RoutePoint,
    x: number,
    y: number,
  ) => {
    target.push(
      point.x,
      point.y,
      z,
      previous.x,
      previous.y,
      z,
      next.x,
      next.y,
      z,
      x,
      y,
    );
  };
  const side = (index: number, y: number) =>
    vertex(
      line,
      points[index],
      points[Math.max(0, index - 1)],
      points[Math.min(points.length - 1, index + 1)],
      0,
      y,
    );
  for (let index = 0; index + 1 < points.length; index++) {
    side(index, -1);
    side(index, 1);
    side(index + 1, -1);
    side(index + 1, -1);
    side(index, 1);
    side(index + 1, 1);
  }
  if (points.length > 1) {
    for (const index of [0, points.length - 1]) {
      const point = points[index];
      const previous = points[Math.max(0, index - 1)];
      const next = points[Math.min(points.length - 1, index + 1)];
      const start = index === 0 ? Math.PI / 2 : -Math.PI / 2;
      for (let step = 0; step < 10; step++) {
        vertex(line, point, previous, next, 0, 0);
        for (const angle of [
          start + (step * Math.PI) / 10,
          start + ((step + 1) * Math.PI) / 10,
        ]) {
          vertex(line, point, previous, next, Math.cos(angle), Math.sin(angle));
        }
      }
    }
  }
  const straight = segments
    .map((segment, index) => ({
      ...segment,
      index,
      length: Math.hypot(
        (segment.end.x - segment.start.x) * geometry.width,
        (segment.end.y - segment.start.y) * geometry.height,
      ),
    }))
    .filter((segment) => !segment.control && segment.length > 10);
  const interior = straight.filter(
    (segment) => segment.index > 0 && segment.index < segments.length - 1,
  );
  const chosen = (interior.length ? interior : straight).sort(
    (a, b) => b.length - a.length,
  )[0];
  if (chosen) {
    const start = source(chosen.start);
    const end = source(chosen.end);
    const midpoint = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };
    // An outer white triangle and smaller coloured triangle preserve direction
    // at any camera angle, with dimensions in CSS pixels like the SVG arrow.
    for (const [x, y] of [
      [10, 0],
      [-7, 8],
      [-7, -8],
      [7, 0],
      [-5, 5],
      [-5, -5],
    ]) {
      vertex(arrow, midpoint, start, end, x, y);
    }
  }
  return { line: new Float32Array(line), arrow: new Float32Array(arrow) };
}

interface Program {
  value: WebGLProgram;
  attributes: Record<string, number>;
  uniforms: Record<string, WebGLUniformLocation>;
}

interface Mesh {
  buffer: WebGLBuffer;
  count: number;
}

export interface VenueMapWebGLRenderer {
  /** Apply temporary route styling without recreating the graphics context. */
  updateMap(map: MapDocument): void;
  /** Refresh lettering in place when decoded logo batches arrive. */
  updateLogos(logos?: VenueMapLogos): void;
  render(
    camera: VenueMapCamera,
    viewport: VenueMapWebGLViewport,
    route?: VenueMapRouteData,
    selectedStandId?: string,
  ): void;
  dispose(options?: { loseContext?: boolean }): void;
}

/** No animation loop: one bounded draw when the shared camera or route changes. */
export function createVenueMapWebGL(
  canvas: HTMLCanvasElement,
  map: MapDocument,
  logos?: VenueMapLogos,
): VenueMapWebGLRenderer {
  let currentMap = map;
  let currentLogos = logos;
  let geometry = map.geometry;
  const sourceMap = mapAppearanceSourceDocument(map);
  const gl = canvas.getContext("webgl", {
    alpha: true,
    depth: true,
    stencil: false,
    antialias: true,
    premultipliedAlpha: true,
    preserveDrawingBuffer: false,
    powerPreference: "low-power",
  });
  if (!gl) throw new Error("WebGL is unavailable");
  const buffers: WebGLBuffer[] = [];
  const textures: WebGLTexture[] = [];
  const shaders: WebGLShader[] = [];
  const programs: WebGLProgram[] = [];
  const loseContext = gl.getExtension("WEBGL_lose_context");
  let disposed = false;
  let contextReleased = false;
  let enabledAttributes: number[] = [];
  const dispose = (options: { loseContext?: boolean } = {}) => {
    if (!disposed) {
      disposed = true;
      for (const location of enabledAttributes)
        gl.disableVertexAttribArray(location);
      enabledAttributes = [];
      gl.useProgram(null);
      gl.bindBuffer(gl.ARRAY_BUFFER, null);
      gl.bindTexture(gl.TEXTURE_2D, null);
      for (const buffer of buffers) gl.deleteBuffer(buffer);
      for (const texture of textures) gl.deleteTexture(texture);
      for (const shader of shaders) gl.deleteShader(shader);
      for (const program of programs) gl.deleteProgram(program);
      canvas.width = 1;
      canvas.height = 1;
    }
    // Draft edits rebuild resources on the existing canvas/context. Losing it
    // during a refresh would make the next renderer permanently fall back.
    // The final unmount still releases the context, even after resource disposal.
    if (options.loseContext !== false && !contextReleased) {
      contextReleased = true;
      loseContext?.loseContext();
    }
  };
  try {
    const maxTexture = Math.min(
      2048,
      gl.getParameter(gl.MAX_TEXTURE_SIZE) as number,
    );
    const maxViewport = gl.getParameter(gl.MAX_VIEWPORT_DIMS) as Int32Array;
    const maxBuffer = Math.min(
      4096,
      gl.getParameter(gl.MAX_RENDERBUFFER_SIZE) as number,
      maxViewport[0],
      maxViewport[1],
    );
    const makeShader = (type: number, source: string) => {
      const shader = gl.createShader(type);
      if (!shader) throw new Error("Unable to allocate map shader");
      shaders.push(shader);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS))
        throw new Error(
          gl.getShaderInfoLog(shader) || "Map shader compilation failed",
        );
      return shader;
    };
    const makeProgram = (
      vertex: string,
      fragment: string,
      attributes: readonly string[],
      uniforms: readonly string[],
    ): Program => {
      const program = gl.createProgram();
      if (!program) throw new Error("Unable to allocate map program");
      programs.push(program);
      gl.attachShader(program, makeShader(gl.VERTEX_SHADER, vertex));
      gl.attachShader(program, makeShader(gl.FRAGMENT_SHADER, fragment));
      gl.linkProgram(program);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS))
        throw new Error(
          gl.getProgramInfoLog(program) || "Map program linking failed",
        );
      const locations: Program = {
        value: program,
        attributes: {},
        uniforms: {},
      };
      for (const name of attributes) {
        const location = gl.getAttribLocation(program, name);
        if (location < 0) throw new Error(`Missing map attribute ${name}`);
        locations.attributes[name] = location;
      }
      for (const name of uniforms) {
        const location = gl.getUniformLocation(program, name);
        if (location === null) throw new Error(`Missing map uniform ${name}`);
        locations.uniforms[name] = location;
      }
      return locations;
    };
    const solid = makeProgram(
      solidVertex,
      solidFragment,
      ["a_position", "a_normal", "a_color"],
      ["u_projection", "u_light", "u_eye", "u_opacity"],
    );
    const uvPrecision = gl.getShaderPrecisionFormat(
      gl.FRAGMENT_SHADER,
      gl.HIGH_FLOAT,
    )?.precision
      ? "highp"
      : "mediump";
    const label = makeProgram(
      labelVertex.replaceAll("UV_PRECISION", uvPrecision),
      labelFragment.replaceAll("UV_PRECISION", uvPrecision),
      ["a_position", "a_uv", "a_ink", "a_opacity"],
      ["u_projection", "u_atlas"],
    );
    const entrance = makeProgram(
      entranceVertex.replaceAll("UV_PRECISION", uvPrecision),
      entranceFragment.replaceAll("UV_PRECISION", uvPrecision),
      ["a_position", "a_offset", "a_uv", "a_opacity"],
      ["u_projection", "u_viewport", "u_scale", "u_atlas"],
    );
    const line = makeProgram(
      routeVertex,
      routeFragment,
      ["a_position", "a_previous", "a_next", "a_offset"],
      [
        "u_projection",
        "u_viewport",
        "u_width",
        "u_color",
        "u_arrow",
        "u_surfaceDepth",
      ],
    );
    const makeMesh = (vertices: Float32Array, stride: number): Mesh => {
      const buffer = gl.createBuffer();
      if (!buffer) throw new Error("Unable to allocate map buffer");
      buffers.push(buffer);
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);
      return { buffer, count: vertices.length / stride };
    };
    const anisotropy = gl.getExtension("EXT_texture_filter_anisotropic");
    const maxAnisotropy = anisotropy
      ? Math.min(
          4,
          gl.getParameter(anisotropy.MAX_TEXTURE_MAX_ANISOTROPY_EXT) as number,
        )
      : 1;
    const uploadAtlas = (
      image: HTMLCanvasElement,
      premultiplied = false,
      existing?: WebGLTexture,
    ) => {
      try {
        const texture = existing || gl.createTexture();
        if (!texture) throw new Error("Unable to allocate map lettering");
        if (!existing) textures.push(texture);
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, premultiplied);
        gl.texImage2D(
          gl.TEXTURE_2D,
          0,
          gl.RGBA,
          gl.RGBA,
          gl.UNSIGNED_BYTE,
          image,
        );
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(
          gl.TEXTURE_2D,
          gl.TEXTURE_MIN_FILTER,
          gl.LINEAR_MIPMAP_LINEAR,
        );
        gl.generateMipmap(gl.TEXTURE_2D);
        if (anisotropy) {
          gl.texParameterf(
            gl.TEXTURE_2D,
            anisotropy.TEXTURE_MAX_ANISOTROPY_EXT,
            maxAnisotropy,
          );
        }
        return texture;
      } finally {
        // Uploaded pixels are retained by GL; release the CPU bitmap even if
        // allocation fails and the component falls back to SVG.
        image.width = 1;
        image.height = 1;
      }
    };
    const {
      floor: floorVertices,
      artwork: artworkVertices,
      stands: standVertices,
    } = buildVenueMapWebGLMeshes(geometry);
    const floor = makeMesh(floorVertices, 10);
    const artwork = artworkVertices.length
      ? makeMesh(artworkVertices, 10)
      : undefined;
    const stands = makeMesh(standVertices, 10);
    let standDrawRanges = venueMapWebGLStandDrawRanges(standVertices);
    let atlas = buildVenueMapWebGLLabelAtlas(map, maxTexture, logos);
    const texture = uploadAtlas(atlas.canvas, true);
    const labels = makeMesh(atlas.vertices, 9);
    const entranceAtlas = buildVenueMapWebGLEntranceAtlas(map, maxTexture);
    const entranceTexture = uploadAtlas(entranceAtlas.canvas, true);
    const entrances = makeMesh(entranceAtlas.vertices, 8);
    const routeLine = makeMesh(new Float32Array(), 11);
    const routeArrow = makeMesh(new Float32Array(), 11);
    let previousPoints: VenueMapRouteData["points"] | undefined;
    let previousSelection: string | undefined;
    let selectionDirty = false;
    let selection: Mesh | undefined;
    if (gl.getError() !== gl.NO_ERROR)
      throw new Error("Unable to initialize map graphics resources");

    const attribute = (
      program: Program,
      name: string,
      size: number,
      stride: number,
      offset: number,
    ) => {
      const location = program.attributes[name];
      gl.enableVertexAttribArray(location);
      gl.vertexAttribPointer(
        location,
        size,
        gl.FLOAT,
        false,
        stride * 4,
        offset * 4,
      );
    };
    const useProgram = (program: Program, projection: Float32Array) => {
      for (const location of enabledAttributes)
        gl.disableVertexAttribArray(location);
      enabledAttributes = Object.values(program.attributes);
      gl.useProgram(program.value);
      gl.uniformMatrix4fv(program.uniforms.u_projection, false, projection);
    };
    const drawSolid = (
      mesh: Mesh,
      opacity = 1,
      first = 0,
      count = mesh.count,
    ) => {
      gl.bindBuffer(gl.ARRAY_BUFFER, mesh.buffer);
      attribute(solid, "a_position", 3, 10, 0);
      attribute(solid, "a_normal", 3, 10, 3);
      attribute(solid, "a_color", 4, 10, 6);
      gl.uniform1f(solid.uniforms.u_opacity, opacity);
      gl.drawArrays(gl.TRIANGLES, first, count);
    };
    const drawLabels = (
      projection: Float32Array,
      first: number,
      count: number,
    ) => {
      // Premultiplied filtering retains logo colours at translucent edges.
      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
      useProgram(label, projection);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.uniform1i(label.uniforms.u_atlas, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, labels.buffer);
      attribute(label, "a_position", 3, 9, 0);
      attribute(label, "a_uv", 2, 9, 3);
      attribute(label, "a_ink", 3, 9, 5);
      attribute(label, "a_opacity", 1, 9, 8);
      gl.drawArrays(gl.TRIANGLES, first, count);
    };
    const bindLine = (mesh: Mesh) => {
      gl.bindBuffer(gl.ARRAY_BUFFER, mesh.buffer);
      attribute(line, "a_position", 3, 11, 0);
      attribute(line, "a_previous", 3, 11, 3);
      attribute(line, "a_next", 3, 11, 6);
      attribute(line, "a_offset", 2, 11, 9);
    };
    return {
      dispose,
      updateMap(nextMap) {
        if (disposed) throw new Error("Map renderer was disposed");
        if (nextMap === currentMap) return;
        if (mapAppearanceSourceDocument(nextMap) !== sourceMap) {
          throw new Error(
            "A new source map requires new map graphics resources",
          );
        }
        const standRanges = updateVenueMapWebGLStandVertices(
          standVertices,
          nextMap.geometry,
        );
        if (standRanges.length)
          standDrawRanges = venueMapWebGLStandDrawRanges(standVertices);
        const labelRanges = updateVenueMapWebGLLabelVertices(atlas, nextMap);
        for (const [mesh, data, ranges] of [
          [stands, standVertices, standRanges],
          [labels, atlas.vertices, labelRanges],
        ] as const) {
          if (!ranges.length) continue;
          gl.bindBuffer(gl.ARRAY_BUFFER, mesh.buffer);
          // Adjacent changed booths/tiles share one transfer. Unchanged gaps
          // stay untouched, so replacing endpoints uploads only their spans.
          for (let index = 0; index < ranges.length; index++) {
            const start = ranges[index].start;
            let end = ranges[index].end;
            while (ranges[index + 1]?.start === end) end = ranges[++index].end;
            gl.bufferSubData(
              gl.ARRAY_BUFFER,
              start * Float32Array.BYTES_PER_ELEMENT,
              data.subarray(start, end),
            );
          }
        }
        if (previousSelection) {
          const before = geometry.stands.find(
            (stand) => stand.id === previousSelection,
          );
          const after = nextMap.geometry.stands.find(
            (stand) => stand.id === previousSelection,
          );
          selectionDirty ||= Boolean(
            before && after && mapStandHeight(before) !== mapStandHeight(after),
          );
        }
        geometry = nextMap.geometry;
        currentMap = nextMap;
      },
      updateLogos(nextLogos) {
        if (disposed) throw new Error("Map renderer was disposed");
        if (nextLogos === currentLogos) return;
        if (
          (nextLogos?.size || 0) === (currentLogos?.size || 0) &&
          [...(nextLogos || [])].every(
            ([id, asset]) => currentLogos?.get(id) === asset,
          )
        ) {
          currentLogos = nextLogos;
          return;
        }
        // Lettering can change layout when a logo appears, but geometry,
        // programs, entrance labels and the texture object remain reusable.
        const nextAtlas = buildVenueMapWebGLLabelAtlas(
          currentMap,
          maxTexture,
          nextLogos,
        );
        uploadAtlas(nextAtlas.canvas, true, texture);
        gl.bindBuffer(gl.ARRAY_BUFFER, labels.buffer);
        gl.bufferData(gl.ARRAY_BUFFER, nextAtlas.vertices, gl.STATIC_DRAW);
        // Texture reallocations can fail without throwing (notably under mobile
        // GPU memory pressure). Check only this rare allocation path, never
        // camera draws or bounded route-style buffer updates.
        if (gl.getError() !== gl.NO_ERROR)
          throw new Error("Unable to update map lettering resources");
        labels.count = nextAtlas.vertices.length / 9;
        atlas = nextAtlas;
        currentLogos = nextLogos;
      },
      render(camera, viewport, route, selectedStandId) {
        if (disposed) throw new Error("Map renderer was disposed");
        const size = venueMapWebGLBufferSize(
          viewport.width,
          viewport.height,
          window.devicePixelRatio || 1,
        );
        const hardwareScale = Math.min(
          1,
          maxBuffer / size.width,
          maxBuffer / size.height,
        );
        const width = Math.max(1, Math.floor(size.width * hardwareScale));
        const height = Math.max(1, Math.floor(size.height * hardwareScale));
        if (canvas.width !== width || canvas.height !== height) {
          canvas.width = width;
          canvas.height = height;
        }
        if (route?.points !== previousPoints) {
          const meshes = route
            ? buildVenueMapWebGLRoute(route, geometry)
            : { line: new Float32Array(), arrow: new Float32Array() };
          for (const [mesh, data] of [
            [routeLine, meshes.line],
            [routeArrow, meshes.arrow],
          ] as const) {
            gl.bindBuffer(gl.ARRAY_BUFFER, mesh.buffer);
            gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
            mesh.count = data.length / 11;
          }
          previousPoints = route?.points;
        }
        if (selectionDirty || selectedStandId !== previousSelection) {
          const vertices = buildVenueMapWebGLSelection(
            geometry,
            selectedStandId,
          );
          if (selection) {
            gl.bindBuffer(gl.ARRAY_BUFFER, selection.buffer);
            gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);
            selection.count = vertices.length / 11;
          } else if (vertices.length) {
            selection = makeMesh(vertices, 11);
          }
          previousSelection = selectedStandId;
          selectionDirty = false;
        }
        const lighting = venueMapLighting(camera);
        const projection = venueMapWebGLProjection(camera, viewport, geometry);
        gl.viewport(0, 0, width, height);
        gl.clearColor(0, 0, 0, 0);
        gl.clearDepth(1);
        gl.depthMask(true);
        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
        gl.enable(gl.DEPTH_TEST);
        gl.depthFunc(gl.LEQUAL);
        gl.disable(gl.CULL_FACE);
        gl.enable(gl.BLEND);
        // Preserve premultiplied destination alpha for browser compositing.
        gl.blendFuncSeparate(
          gl.SRC_ALPHA,
          gl.ONE_MINUS_SRC_ALPHA,
          gl.ONE,
          gl.ONE_MINUS_SRC_ALPHA,
        );

        useProgram(solid, projection);
        const { direction } = lighting;
        gl.uniform3f(
          solid.uniforms.u_light,
          direction.x,
          direction.y,
          direction.z,
        );
        gl.uniform4f(
          solid.uniforms.u_eye,
          ...venueMapWebGLViewPosition(camera, geometry),
        );
        drawSolid(floor);
        if (artwork?.count) {
          // Source paint is ordered, including white cutouts and translucent
          // fills. Keep a single floor plane and painter order instead of tiny
          // depth steps that flicker or eventually cross a flat booth roof.
          gl.depthMask(false);
          drawSolid(artwork);
          gl.depthMask(true);
        }
        if (atlas.groundVertexCount) {
          gl.depthMask(false);
          drawLabels(projection, 0, atlas.groundVertexCount);
          gl.depthMask(true);
          gl.blendFuncSeparate(
            gl.SRC_ALPHA,
            gl.ONE_MINUS_SRC_ALPHA,
            gl.ONE,
            gl.ONE_MINUS_SRC_ALPHA,
          );
          useProgram(solid, projection);
        }
        if (!standDrawRanges.translucent.length) drawSolid(stands);
        else {
          // Public maps retain their single opaque draw. Editor ghosts blend
          // over all opaque booths first and never occlude later ghost faces.
          for (const range of standDrawRanges.opaque)
            drawSolid(stands, 1, range.first, range.count);
          const depth = (point: readonly [number, number, number]) => {
            const [x, y, z] = point;
            return (
              (projection[2] * x +
                projection[6] * y +
                projection[10] * z +
                projection[14]) /
              (projection[3] * x +
                projection[7] * y +
                projection[11] * z +
                projection[15])
            );
          };
          standDrawRanges.translucent.sort(
            (a, b) => depth(b.center) - depth(a.center),
          );
          gl.depthMask(false);
          for (const range of standDrawRanges.translucent)
            drawSolid(stands, 1, range.first, range.count);
        }

        gl.depthMask(false);
        if (routeLine.count) {
          useProgram(line, projection);
          gl.uniform1f(line.uniforms.u_surfaceDepth, 0);
          gl.uniform2f(
            line.uniforms.u_viewport,
            Math.max(1, viewport.width),
            Math.max(1, viewport.height),
          );
          gl.uniform1f(line.uniforms.u_arrow, 0);
          bindLine(routeLine);
          for (const [halfWidth, red, green, blue, alpha] of [
            [4.5, 1, 1, 1, 1],
            [2.5, 198 / 255, 0, 108 / 255, 1],
            [0.5, 243 / 255, 91 / 255, 170 / 255, 0.8],
          ]) {
            gl.uniform1f(line.uniforms.u_width, halfWidth);
            gl.uniform4f(line.uniforms.u_color, red, green, blue, alpha);
            gl.drawArrays(gl.TRIANGLES, 0, routeLine.count);
          }
          if (routeArrow.count) {
            bindLine(routeArrow);
            gl.uniform1f(line.uniforms.u_arrow, 1);
            gl.uniform1f(line.uniforms.u_width, 1);
            gl.uniform4f(line.uniforms.u_color, 1, 1, 1, 1);
            gl.drawArrays(gl.TRIANGLES, 0, 3);
            gl.uniform4f(line.uniforms.u_color, 183 / 255, 0, 100 / 255, 1);
            gl.drawArrays(gl.TRIANGLES, 3, 3);
          }
        }

        // Selection uses one tiny reusable ribbon mesh. Retain depth testing
        // and draw before labels, so neither neighbouring booths nor lettering
        // is painted over by a screen-space overlay.
        if (selection?.count) {
          const selectedStand = geometry.stands.find(
            (stand) => stand.id === selectedStandId,
          );
          const opacity = selectedStand ? mapPreviewOpacity(selectedStand) : 1;
          useProgram(line, projection);
          gl.uniform1f(line.uniforms.u_surfaceDepth, 1);
          gl.uniform2f(
            line.uniforms.u_viewport,
            Math.max(1, viewport.width),
            Math.max(1, viewport.height),
          );
          gl.uniform1f(line.uniforms.u_arrow, 0);
          bindLine(selection);
          for (const [halfWidth, red, green, blue] of [
            [3.25, 1, 1, 1],
            [1.5, 198 / 255, 0, 108 / 255],
          ]) {
            gl.uniform1f(line.uniforms.u_width, halfWidth);
            gl.uniform4f(line.uniforms.u_color, red, green, blue, opacity);
            gl.drawArrays(gl.TRIANGLES, 0, selection.count);
          }
        }

        drawLabels(
          projection,
          atlas.groundVertexCount,
          labels.count - atlas.groundVertexCount,
        );

        // Like the former overlay, entrance names sit above the scene and do
        // not foreshorten. Clearing this canvas clears their previous position.
        gl.disable(gl.DEPTH_TEST);
        // Premultiplied atlas filtering preserves the coloured text/halo edges.
        gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
        useProgram(entrance, projection);
        gl.uniform2f(
          entrance.uniforms.u_viewport,
          Math.max(1, viewport.width),
          Math.max(1, viewport.height),
        );
        gl.uniform1f(
          entrance.uniforms.u_scale,
          camera.imageWidth / geometry.width,
        );
        gl.bindTexture(gl.TEXTURE_2D, entranceTexture);
        gl.uniform1i(entrance.uniforms.u_atlas, 0);
        gl.bindBuffer(gl.ARRAY_BUFFER, entrances.buffer);
        attribute(entrance, "a_position", 3, 8, 0);
        attribute(entrance, "a_offset", 2, 8, 3);
        attribute(entrance, "a_uv", 2, 8, 5);
        attribute(entrance, "a_opacity", 1, 8, 7);
        gl.drawArrays(gl.TRIANGLES, 0, entrances.count);
        // Draws can originate in a layout effect rather than an animation
        // callback. Submit promptly without a synchronous finish/readback.
        gl.flush();
      },
    };
  } catch (error) {
    dispose();
    throw error;
  }
}
